import React, { useState, useEffect, useCallback, useRef } from 'react'

/**
 * PAGINATION FIX IMPLEMENTATION
 *
 * Updated pagination to match the VehicleInventory implementation:
 * 1. Using TablePagination component for consistent UI
 * 2. Moved loadRiders into a useCallback to ensure consistent dependencies
 * 3. Improved error handling and state management for pagination
 * 4. Added enhanced logging for better debugging
 *
 * To debug pagination issues:
 * - Check browser console for detailed request/response logs
 * - Verify that page parameters are correctly passed to the backend
 * - Ensure backend is returning the correct page of results
 */
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  Avatar,
  Stack,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  Upload as UploadIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { riderService, Rider } from '../services'
import { vehicleService } from '../services/vehicleService'
import { useAuth } from '../contexts/AuthContext'
import EnhancedRiderForm from '../components/EnhancedRiderForm'
import TelecallerAssignmentDialog from '../components/riders/TelecallerAssignmentDialog'
import { DirectionsBike } from "@mui/icons-material";
import {
  Download as DownloadIcon,
} from '@mui/icons-material'

import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { normalizeIndiaCityName } from '../utils/indiaCityMap'
// import { useTheme } from "@mui/material/styles";


const REGISTRATION_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
  { value: 'incomplete', label: 'Incomplete', color: 'default' },
]

const KYC_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'incomplete', label: 'Incomplete', color: 'default' },
  { value: 'submitted', label: 'Submitted', color: 'info' },
  { value: 'review', label: 'Under Review', color: 'warning' },
  { value: 'approved', label: 'Approved', color: 'success' },
  { value: 'verified', label: 'Verified', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
]

const RIDER_MANAGEMENT_STATE_KEY = 'ev91:rider-management:state'

const normalizeGatewayApiBase = (rawUrl?: string) => {
  const fallback = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api'
  const base = (rawUrl || fallback).replace(/\/+$/, '')

  return base
    .replace(/\/api\/v1\/riders\/api$/i, '/api')
    .replace(/\/api\/v1\/riders$/i, '/api')
    .replace(/\/api\/riders$/i, '/api')
    .replace(/\/riders$/i, '/api')
    .replace(/\/api\/v1$/i, '/api')
}

const gatewayApiUrl = (path: string) => {
  const base = normalizeGatewayApiBase(
    import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL
  )
  return `${base}${path.startsWith('/') ? path : `/${path}`}`
}

const getCityQueryValues = (
  selectedCityNames: string[],
  availableCities: Array<{ name: string }>,
) => {
  const selectedCityKeys = new Set(selectedCityNames.map(normalizeIndiaCityName))
  const matchingCityNames = availableCities
    .filter((city) => selectedCityKeys.has(normalizeIndiaCityName(city.name)))
    .map((city) => city.name)

  return Array.from(new Set([...selectedCityNames, ...matchingCityNames]))
}

interface RiderManagementStoredState {
  searchInput?: string
  searchTerm?: string
  kycStatusFilter?: string
  activeStatusFilter?: string
  cityFilter?: string | string[]
  vehicleTypeFilter?: string
  referredByFilter?: string
  dateFromFilter?: string
  dateToFilter?: string
  page?: number
  rowsPerPage?: number
}

const getStoredRiderManagementState = (): RiderManagementStoredState => {
  if (typeof window === 'undefined') return {}

  try {
    const rawState = window.sessionStorage.getItem(RIDER_MANAGEMENT_STATE_KEY)
    return rawState ? JSON.parse(rawState) : {}
  } catch {
    return {}
  }
}

const normalizeAccessValue = (value?: string | null) =>
  (value || '').trim().toLowerCase().replace(/[_-]+/g, ' ')

const canExportRiderDirectory = (user: any) => {
  if (!user) return false

  const roleNames = (user.roles || []).map((role: any) => normalizeAccessValue(role?.name))
  const departmentName = normalizeAccessValue(user.employee?.department?.name || user.department?.name)
  const departmentCode = normalizeAccessValue(user.employee?.department?.code || user.department?.code)
  const teamName = normalizeAccessValue(user.employee?.team?.name || user.team?.name)
  const teamCode = normalizeAccessValue(user.employee?.team?.code || user.team?.code)
  const orgLabels = [departmentName, departmentCode, teamName, teamCode]

  const isSuperAdmin = roleNames.some(
    (role: string) => role === 'super admin' || role === 'superadmin'
  )

  const isItTeam = [...roleNames, ...orgLabels].some(
    (label: string) =>
      label === 'it' ||
      label === 'it admin' ||
      label === 'it team' ||
      label.includes('information technology'),
  )

  const isMisTeam = [...roleNames, ...orgLabels].some(
    (label: string) =>
      label === 'mis' ||
      label === 'mis management' ||
      label === 'mis team' ||
      label === 'mis department' ||
      label.includes('management information system'),
  )

  return isSuperAdmin || isItTeam || isMisTeam
}

const canEditReferral = (user: any) => {
  if (!user) return false

  const roleNames = (user.roles || []).map((role: any) => normalizeAccessValue(role?.name))
  const departmentName = normalizeAccessValue(user.employee?.department?.name || user.department?.name)
  const departmentCode = normalizeAccessValue(user.employee?.department?.code || user.department?.code)
  const orgLabels = [departmentName, departmentCode]

  const isAdmin = roleNames.some((role: string) =>
    role === 'admin' || role === 'super admin' || role === 'superadmin'
  )

  const isSupply = roleNames.some((role: string) =>
    role === 'supply admin' || role === 'suppy admin' || role === 'supply'
  ) || orgLabels.some((label: string) =>
    label === 'supply' || label === 'supply admin' || label === 'suppy admin'
  )

  return isAdmin || isSupply
}

interface RiderFormData {
  name: string
  phone: string
  email: string
  dob: string
  address1: string
  address2: string
  city: string
  state: string
  pincode: string
  aadharNumber: string
  panNumber: string
  drivingLicenseNumber: string
  emergencyName: string
  emergencyPhone: string
  emergencyRelation: string
}

const RiderManagement: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const canExportRiders = canExportRiderDirectory(user)
  const storedRiderState = getStoredRiderManagementState()
  const [riders, setRiders] = useState<Rider[]>([])
  const [loading, setLoading] = useState(true)
  // Calculate own/rented vehicle counts from riders
  // const ownVehicleCount = riders.filter(r => r.needsEvRental === false && (r.vehiclePreference === 'OWN_VEHICLE' || r.vehiclePreference === 'CYCLE' || r.vehiclePreference === 'WALK')).length;
  // const rentedVehicleCount = riders.filter(r => r.needsEvRental === true).length;
  const [openDialog, setOpenDialog] = useState(false)
  const [editingRider, setEditingRider] = useState<Rider | null>(null)
  const [telecallerDialogOpen, setTelecallerDialogOpen] = useState(false)
  const [selectedRiderForTelecaller, setSelectedRiderForTelecaller] = useState<Rider | null>(null)
  const [updateReferrerDialogOpen, setUpdateReferrerDialogOpen] = useState(false)
  const [selectedRiderForReferrerUpdate, setSelectedRiderForReferrerUpdate] = useState<Rider | null>(null)
  const [referrerEmailInput, setReferrerEmailInput] = useState('')
  const [updatingReferrer, setUpdatingReferrer] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  })
  
const logger = {
  info: (...args: any[]) => console.info(...args),
  error: (...args: any[]) => console.error(...args),
  warn: (...args: any[]) => console.warn(...args),
  debug: (...args: any[]) => console.debug(...args),
}

// const theme = useTheme();

  // Filters and pagination
  const [searchInput, setSearchInput] = useState(storedRiderState.searchInput || '')
  const [searchTerm, setSearchTerm] = useState(storedRiderState.searchTerm || '')
  const [kycStatusFilter, setKycStatusFilter] = useState(storedRiderState.kycStatusFilter || '')
  const [activeStatusFilter, setActiveStatusFilter] = useState(storedRiderState.activeStatusFilter || '')
  const [cityFilter, setCityFilter] = useState<string[]>(
    Array.isArray(storedRiderState.cityFilter)
      ? storedRiderState.cityFilter
      : storedRiderState.cityFilter
        ? [storedRiderState.cityFilter]
        : [],
  )
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState(storedRiderState.vehicleTypeFilter || '')
  const [referredByFilter, setReferredByFilter] = useState(storedRiderState.referredByFilter || '')
  const [dateFromFilter, setDateFromFilter] = useState(storedRiderState.dateFromFilter || '')
  const [dateToFilter, setDateToFilter] = useState(storedRiderState.dateToFilter || '')
  const [page, setPage] = useState(storedRiderState.page || 0)
  const [rowsPerPage, setRowsPerPage] = useState(storedRiderState.rowsPerPage || 10)
  const [totalCount, setTotalCount] = useState(0)
  const [cities, setCities] = useState<Array<{id: string; name: string}>>([])
  const [loadingCities, setLoadingCities] = useState(false)
  const [referredByOptions, setReferredByOptions] = useState<Array<{id: string; publicRiderId: string | null; name: string | null; phone: string; city: string | null}>>([])
  const [loadingReferredBy, setLoadingReferredBy] = useState(false)
  const latestRiderRequestRef = useRef(0)
  const latestStatsRequestRef = useRef(0)
  const cityQueryValues = getCityQueryValues(cityFilter, cities)
  const [stats, setStats] = useState({
  totalRiders: 0,
  activeRiders: 0,
  pendingKYC: 0,
  ownVehicle: 0,
  rentedVehicle: 0,
  vehicleAssignedRiders: 0,
});

  useEffect(() => {
    window.sessionStorage.setItem(
      RIDER_MANAGEMENT_STATE_KEY,
      JSON.stringify({
        searchInput,
        searchTerm,
        kycStatusFilter,
        activeStatusFilter,
        cityFilter,
        vehicleTypeFilter,
        referredByFilter,
        dateFromFilter,
        dateToFilter,
        page,
        rowsPerPage,
      }),
    )
  }, [
    searchInput,
    searchTerm,
    kycStatusFilter,
    activeStatusFilter,
    cityFilter,
    vehicleTypeFilter,
    referredByFilter,
    dateFromFilter,
    dateToFilter,
    page,
    rowsPerPage,
  ])


  // Enhanced loadRiders function with useCallback for consistent dependency tracking
  // Track when filters are being applied
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);

  const loadRiders = useCallback(async (showLoader = true) => {
    let requestId = 0;

    try {
      if (showLoader) setLoading(true);

      // Check if we have any active filters to show in the UI
      const hasActiveFilters = !!(
        searchTerm ||
        kycStatusFilter ||
        activeStatusFilter ||
        cityFilter.length > 0 ||
        vehicleTypeFilter ||
        referredByFilter ||
        dateFromFilter ||
        dateToFilter
      );
      setFiltersApplied(hasActiveFilters);

      // Create a unique request ID for tracking this request in logs
      requestId = latestRiderRequestRef.current + 1;
      latestRiderRequestRef.current = requestId;
      logger.info('📡 RiderManagement: Loading riders data...', {
        requestId,
        page: page + 1, // Backend uses 1-based pagination
        limit: rowsPerPage,
        filters: {
          search: searchTerm,
          kycStatus: kycStatusFilter,
          isActive: activeStatusFilter,
          city: cityQueryValues,
          vehicleType: vehicleTypeFilter,
          referredBy: referredByFilter,
          dateFrom: dateFromFilter,
          dateTo: dateToFilter,
        },
      });

      const response = await riderService.getRiders({
        page: page + 1, // Backend uses 1-based pagination
        limit: rowsPerPage,
        search: searchTerm || undefined,
        kycStatus: kycStatusFilter || undefined,
        isActive: activeStatusFilter ? activeStatusFilter === 'active' : undefined,
        city: cityQueryValues.length > 0 ? cityQueryValues : undefined,
        vehicleType: vehicleTypeFilter || undefined,
        referredBy: referredByFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      if (requestId !== latestRiderRequestRef.current) {
        logger.debug('Skipping stale riders response', { requestId });
        return;
      }

      logger.info('✅ RiderManagement: Riders loaded:', {
        requestId,
        success: response.success,
        dataCount: response.data?.length,
        pagination: response.pagination
      });

      // Debug: Check what KYC statuses we actually received
      if (response.data) {
        const receivedStatuses = response.data.map(r => r.kycStatus);
        logger.info('📊 RESPONSE - KYC Statuses received:', {
          filter: kycStatusFilter,
          uniqueStatuses: [...new Set(receivedStatuses)],
          sample: response.data.slice(0, 3).map(r => ({
            id: r.id,
            name: r.name,
            kycStatus: r.kycStatus
          }))
        });
      }

      if (response.success) {
        // Enhanced logging to debug filter issues
        const activeRiders = response.data?.filter(r => r.isActive === true);
        const inactiveRiders = response.data?.filter(r => r.isActive === false);

        // Check filter effectiveness by showing counts for each status
        const registrationStatusCounts = response.data?.reduce((acc: Record<string, number>, rider: Rider) => {
          const status = rider.registrationStatus;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        const kycStatusCounts = response.data?.reduce((acc: Record<string, number>, rider: Rider) => {
          const status = rider.kycStatus;
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        }, {});

        logger.info('📋 Setting riders state with data:', {
          count: response.data?.length,
          firstRider: response.data?.[0] ? {
            id: response.data[0].id,
            name: response.data[0].name,
            isActive: response.data[0].isActive,
            registrationStatus: response.data[0].registrationStatus,
            kycStatus: response.data[0].kycStatus,
            // Added vehicle assignment debug info
            assignedVehicleId: response.data[0].assignedVehicleId,
            hasAssignedVehicleObject: !!response.data[0].assignedVehicle,
            assignedVehicleDetails: response.data[0].assignedVehicle ? {
              registrationNumber: response.data[0].assignedVehicle.registrationNumber,
              operationalStatus: response.data[0].assignedVehicle.operationalStatus
            } : null,
            // Telecaller debug info
            assignedTelecaller: (response.data[0] as any)?.assignedTelecaller || null,
            computedAssignedTelecaller: (response.data[0] as any)?.computedAssignedTelecaller || null,
            employeeId: (response.data[0] as any)?.employeeId || null
          } : null,
          // Filter statistics
          activeCount: activeRiders?.length,
          inactiveCount: inactiveRiders?.length,
          // Status counts
          registrationStatusCounts,
          kycStatusCounts,
          // Vehicle assignment summary
          vehicleAssignmentCount: response.data?.filter(r => r.assignedVehicleId).length,
          vehicleObjectCount: response.data?.filter(r => r.assignedVehicle).length,
          // Applied filters
          appliedFilters: {
            kycStatus: kycStatusFilter,
            isActive: activeStatusFilter,
            city: cityFilter,
            vehicleType: vehicleTypeFilter,
          }
        });

        setRiders(response.data);
        setTotalCount(response.pagination?.totalItems ?? (response.pagination as any)?.totalCount ?? 0);

        // Check for data integrity
        const respTotalItems = response.pagination?.totalItems ?? (response.pagination as any)?.totalCount ?? 0;
        if (response.data.length === 0 && respTotalItems > 0 && page > 0) {
          logger.warn('⚠️ Data integrity issue: Empty page with non-zero total count', {
            page: page + 1,
            totalItems: respTotalItems
          });

          // Auto-correct by going back to first page if we're on an empty page
          setPage(0);
          setSnackbar({
            open: true,
            message: 'Showing first page as the requested page had no data.',
            severity: 'info'
          });
        }
      } else {
        logger.error('❌ API returned failure:', response.message);
        setSnackbar({
          open: true,
          message: `Failed to load riders: ${response.message || 'Unknown error'}`,
          severity: 'error'
        });
      }
    } catch (error: any) {
      if (error?.response?.status === 429) {
        setSnackbar({
          open: true,
          message: 'Too many rider requests. Please wait a few seconds and refresh.',
          severity: 'warning'
        });
        return;
      }

      logger.error('❌ Error loading riders:', error);
      setSnackbar({
        open: true,
        message: `Failed to load riders: ${(error as Error).message}`,
        severity: 'error'
      });
    } finally {
      if (requestId === latestRiderRequestRef.current) {
        setLoading(false);
      }
    }
  }, [page, rowsPerPage, searchTerm, kycStatusFilter, activeStatusFilter, cityFilter, cities, vehicleTypeFilter, referredByFilter, dateFromFilter, dateToFilter]);

  const applySearch = useCallback(() => {
    const trimmedSearch = searchInput.trim();
    setSearchTerm((current) => {
      if (current === trimmedSearch) return current;
      setPage(0);
      return trimmedSearch;
    });
  }, [searchInput]);

  const loadStats = useCallback(async () => {
    const statsRequestId = latestStatsRequestRef.current + 1
    latestStatsRequestRef.current = statsRequestId

    try {
      const hasActiveFilters = !!(
        searchTerm ||
        kycStatusFilter ||
        activeStatusFilter ||
        cityFilter.length > 0 ||
        vehicleTypeFilter ||
        referredByFilter ||
        dateFromFilter ||
        dateToFilter
      )

      if (hasActiveFilters) {
        const statsResponse = await riderService.getRiderStats({
          search: searchTerm || undefined,
          kycStatus: kycStatusFilter || undefined,
          isActive: activeStatusFilter ? activeStatusFilter === 'active' : undefined,
          city: cityQueryValues.length > 0 ? cityQueryValues : undefined,
          vehicleType: vehicleTypeFilter || undefined,
          referredBy: referredByFilter || undefined,
          dateFrom: dateFromFilter || undefined,
          dateTo: dateToFilter || undefined,
        })

        if (statsRequestId !== latestStatsRequestRef.current) {
          return
        }

        if (!statsResponse.success || !statsResponse.data) {
          throw new Error(statsResponse.message || 'Failed to load filtered rider counts')
        }

        const pendingKYCFromApi = statsResponse.data.registrationStatusCounts?.kycPending ?? statsResponse.data.pendingKYC ?? 0

        setStats({
          totalRiders: statsResponse.data.totalRiders,
          activeRiders: statsResponse.data.activeRiders,
          pendingKYC: pendingKYCFromApi,
          ownVehicle: statsResponse.data.ownVehicle ?? 0,
          rentedVehicle: statsResponse.data.rentedVehicle ?? 0,
          vehicleAssignedRiders: statsResponse.data.vehicleAssignedRiders ?? 0,
        })
        // Sync the top header total count with filtered total
        setTotalCount(statsResponse.data.totalRiders ?? (statsResponse.data as any).total ?? 0)
        window.dispatchEvent(new CustomEvent('riderStatsUpdated'))
        return
      }

const [evCountResponse, icCountResponse, statsResponse] = await Promise.all([
  fetch(gatewayApiUrl('/v1/additional-apis/ev-vehicle-count'), {
    credentials: 'include'
  })
    .then(async (r) => {
      const data = await r.json()

      if (!r.ok) {
        throw new Error(data?.message || `HTTP Error: ${r.status}`)
      }

      return data
    }),

  fetch(gatewayApiUrl('/v1/additional-apis/ic-vehicle-count'), {
    credentials: 'include'
  })
    .then(async (r) => {
      const data = await r.json()

      if (!r.ok) {
        throw new Error(data?.message || `HTTP Error: ${r.status}`)
      }

      return data
    }),

  riderService.getRiderStats()
]);

      if (statsRequestId !== latestStatsRequestRef.current) {
        return
      }

        if (statsResponse.success) {
        // EV vehicles = Rented vehicles, IC vehicles = Own vehicles
        const rentedVehicleCount = evCountResponse?.data?.count || 0;
        const ownVehicleCount = icCountResponse?.data?.count || 0;
        
        // backend returns pending KYC under registrationStatusCounts.kycPending
        const pendingKYCFromApi = statsResponse.data?.registrationStatusCounts?.kycPending ?? statsResponse.data?.pendingKYC ?? 0;

        setStats({
          totalRiders: statsResponse.data.totalRiders,
          activeRiders: statsResponse.data.activeRiders,
          pendingKYC: pendingKYCFromApi,
          ownVehicle: ownVehicleCount,
          rentedVehicle: rentedVehicleCount,
          vehicleAssignedRiders: statsResponse.data?.vehicleAssignedRiders ?? 0,
        })
        // Sync header total with stats API
        setTotalCount(statsResponse.data.totalRiders ?? (statsResponse.data as any).total ?? 0)
        window.dispatchEvent(new CustomEvent('riderStatsUpdated'))
      }
    } catch (error) {
      logger.error('Error loading rider stats:', error)
    }
  }, [searchTerm, kycStatusFilter, activeStatusFilter, cityFilter, cities, vehicleTypeFilter, referredByFilter, dateFromFilter, dateToFilter])

  // Fetch cities for dropdown
  const loadCities = useCallback(async () => {
    try {
      setLoadingCities(true);
      const data = await vehicleService.getOperationalCities();
      if (data.success && data.data) {
        setCities(data.data.map((city: any) => ({
          id: city.id,
          name: city.name
        })));
      }
    } catch (error) {
      logger.error('Error loading cities:', error);
    } finally {
      setLoadingCities(false);
    }
  }, []);

  // Fetch riders who have referred others (for referred-by dropdown)
  const loadReferredBy = useCallback(async () => {
    try {
      setLoadingReferredBy(true);

      const loadFromService = async () => {
        const response = await riderService.getReferredByList();
        console.log('🔄 riderService.getReferredByList response:', response);
        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          return response.data;
        }
        return [];
      };

      const fallbackFromRiders = async () => {
        const ridersResponse = await riderService.getRiders({ page: 1, limit: 1000 });
        if (ridersResponse.success && Array.isArray(ridersResponse.data)) {
          const uniqueIds = new Map<string, { id: string; publicRiderId: string | null; name: string | null; phone: string; city: string | null }>();
          ridersResponse.data.forEach((rider) => {
            if (rider.referredById) {
              uniqueIds.set(rider.referredById, {
                id: rider.referredById,
                publicRiderId: null,
                name: rider.referredById,
                phone: '',
                city: null,
              });
            }
          });
          return Array.from(uniqueIds.values());
        }
        return [];
      };

      let options = await loadFromService();
      if (!options.length) {
        console.warn('No referred-by options from guaranteed service endpoint, falling back to riders list');
        options = await fallbackFromRiders();
      }

      if (options.length) {
        setReferredByOptions(options);
        console.log('✅ Loaded referred-by riders:', options.length, options);
      } else {
        logger.warn('No referred-by options could be loaded from any source');
        setReferredByOptions([]);
      }
    } catch (error) {
      logger.error('Error loading referred-by list:', error);
      setReferredByOptions([]);
    } finally {
      setLoadingReferredBy(false);
    }
  }, []);

  // Stores dropdown removed for now; re-add when needed

  // Effects for data loading
  useEffect(() => {
    // Initial dropdown data loading on component mount
    loadCities();
    loadReferredBy();
  }, [loadCities, loadReferredBy]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      applySearch();
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [applySearch]);

  // Separate effect for riders that responds to filter changes
  useEffect(() => {
    loadRiders();
  }, [loadRiders]);

  // Debug effect to track pagination changes
  useEffect(() => {
   logger.info('🔍 Pagination Debug:', {
      totalRiders: totalCount,
      currentPageRiders: riders.length,
      currentPage: page,
      rowsPerPage: rowsPerPage,
      backendPagination: true
    });
  }, [page, rowsPerPage, riders, totalCount]);

const handleExportRiders = async () => {
  if (!canExportRiders) {
    setSnackbar({
      open: true,
      message: 'Only Super Admin, IT_Admin, and MIS_Management can export riders.',
      severity: 'error',
    })
    return
  }

  try {
    setLoading(true)

    let allRiders: any[] = []
    let currentPage = 1
    let totalPages = 1

    // Fetch all pages
    while (currentPage <= totalPages) {
      const response = await riderService.getRiders({
        page: currentPage,
        limit: 100,
        search: searchTerm || undefined,
        kycStatus: kycStatusFilter || undefined,
        isActive: activeStatusFilter ? activeStatusFilter === 'active' : undefined,
        city: cityFilter.length > 0 ? cityFilter : undefined,
        vehicleType: vehicleTypeFilter || undefined,
        referredBy: referredByFilter || undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch riders')
      }

      // Add current page riders
      allRiders = [...allRiders, ...response.data]

      // Get total pages
      totalPages = response.pagination?.totalPages || 1

      console.log(
        `Fetched page ${currentPage} of ${totalPages}`
      )

      currentPage++
    }

    console.log('Total riders fetched:', allRiders.length)

    // Format data for Excel
    const exportData = allRiders.map((rider: any) => {
      const vehicleAssignmentStatus = getVehicleAssignmentStatus(rider);
      const vehicleAssignedDate =
        rider.assignedVehicle?.assignedDate ||
        rider.vehicleHistoryAssignedDate;
      const vehicleReturnedDate =
        rider.vehicleReturnedDate ||
        rider.assignedVehicle?.returnedDate;

      return {
        Rider_ID: rider.publicRiderId || rider.id,
        Name: rider.name,
        Phone: rider.phone,
        City: rider.city,
        KYC_Status: rider.kycStatus,
        Client_Name: rider.clientName,
        Client_Rider_ID: rider.clientRiderId || '-',
        Vehicle_Assignment_Status: vehicleAssignmentStatus.label,
        Vehicle_Number:
          rider.assignedVehicle?.registrationNumber ||
          rider.vehicleHistoryRegistrationNumber ||
          '-',
        Vehicle_Assigned_Date: vehicleAssignedDate
          ? new Date(vehicleAssignedDate).toLocaleDateString('en-IN')
          : '-',
        Vehicle_Returned_Date: vehicleReturnedDate
          ? new Date(vehicleReturnedDate).toLocaleDateString('en-IN')
          : '-',

        Vehicle_Type:
          rider.needsEvRental === true
            ? 'EV Vehicle'
            : rider.needsEvRental === false
            ? 'Own Vehicle'
            : 'Unknown',

        Referred_By: rider.referredById || '-',

        Registration_Status: rider.registrationStatus,

        Active_Status: rider.isActive
          ? 'Active'
          : 'Inactive',

        Created_Date: rider.createdAt
          ? new Date(rider.createdAt).toLocaleDateString('en-IN')
          : '-',
      };
    })

    // Create worksheet
    const worksheet =
      XLSX.utils.json_to_sheet(exportData)

    // Create workbook
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      'Riders'
    )

    // Generate excel buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    })

    // Create blob
    const fileData = new Blob([excelBuffer], {
      type:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    })

    // Download file
    saveAs(
      fileData,
      `Riders_${new Date().toISOString().split('T')[0]}.xlsx`
    )

    setSnackbar({
      open: true,
      message: `Successfully exported ${allRiders.length} riders`,
      severity: 'success',
    })
  } 
  
  catch (error) {
    console.error('Export Error:', error)

    setSnackbar({
      open: true,
      message: 'Failed to export riders',
      severity: 'error',
    })
  } finally {
    setLoading(false)
  }
}

  const handleViewRider = useCallback((riderId: string) => {
    navigate(`/rider-management/${riderId}`)
  }, [navigate])

  const handleOpenDialog = useCallback(async (rider?: Rider) => {
    if (rider) {
      try {
        // Fetch full rider details to ensure we have all fields
        const response = await riderService.getRiderById(rider.id)
        if (response.success) {
          const fullRiderData = response.data
          setEditingRider(fullRiderData)
        } else {
          logger.error('Failed to fetch rider details:', response.message)
          // Fallback to existing rider data
          setEditingRider(rider)
        }
      } catch (error) {
        logger.error('Error fetching rider details:', error)
        // Fallback to existing rider data
        setEditingRider(rider)
      }
    } else {
      setEditingRider(null)
    }
    setOpenDialog(true)
  }, [])

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false)
    setEditingRider(null)
  }, [])

  const handleSaveRider = useCallback(async (formData: RiderFormData) => {
    try {
      // Clean the form data before submission
      const cleanedData = {
        ...formData,
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || undefined,
        address1: formData.address1.trim(),
        address2: formData.address2.trim() || undefined,
        city: formData.city.trim(),
        state: formData.state.trim(),
        pincode: formData.pincode.trim(),
        aadharNumber: formData.aadharNumber.trim(),
        panNumber: formData.panNumber.trim().toUpperCase(),
        drivingLicenseNumber: formData.drivingLicenseNumber.trim(),
        emergencyName: formData.emergencyName.trim(),
        emergencyPhone: formData.emergencyPhone.trim(),
        emergencyRelation: formData.emergencyRelation.trim(),
      }

      if (editingRider) {
        await riderService.updateRider(editingRider.id, cleanedData)
        setSnackbar({ open: true, message: 'Rider updated successfully', severity: 'success' })
      } else {
        await riderService.createRider(cleanedData)
        setSnackbar({ open: true, message: 'Rider created successfully', severity: 'success' })
      }
      handleCloseDialog()
      loadRiders()
      loadStats()
    } catch (error) {
      logger.error('Error saving rider:', error)
      setSnackbar({ open: true, message: 'Failed to save rider', severity: 'error' })
    }
  }, [editingRider, loadRiders, loadStats])

  const handleApproveRider = useCallback(async (riderId: string) => {
    if (!window.confirm('Are you sure you want to approve this rider registration?')) return

    try {
      await riderService.approveRider(riderId)
      setSnackbar({ open: true, message: 'Rider approved successfully', severity: 'success' })
      loadRiders()
      loadStats()
    } catch (error) {
      logger.error('Error approving rider:', error)
      setSnackbar({ open: true, message: 'Failed to approve rider', severity: 'error' })
    }
  }, [loadRiders, loadStats])

  const handleRejectRider = useCallback(async (riderId: string) => {
    const reason = window.prompt('Please provide a reason for rejection:')
    if (!reason) return

    try {
      await riderService.rejectRider(riderId, reason)
      setSnackbar({ open: true, message: 'Rider rejected successfully', severity: 'success' })
      loadRiders()
      loadStats()
    } catch (error) {
      logger.error('Error rejecting rider:', error)
      setSnackbar({ open: true, message: 'Failed to reject rider', severity: 'error' })
    }
  }, [loadRiders, loadStats])

  const handleUpdateReferrer = useCallback((riderId: string) => {
    const rider = riders.find(r => r.id === riderId)
    if (rider) {
      setSelectedRiderForReferrerUpdate(rider)
      setReferrerEmailInput('')
      setUpdateReferrerDialogOpen(true)
    }
  }, [riders])

  const handleConfirmUpdateReferrer = useCallback(async () => {
    if (!selectedRiderForReferrerUpdate || !referrerEmailInput.trim()) return

    try {
      setUpdatingReferrer(true)
      await riderService.updateReferredBy(selectedRiderForReferrerUpdate.id, referrerEmailInput.trim())
      setSnackbar({ open: true, message: 'Referrer updated successfully', severity: 'success' })
      setUpdateReferrerDialogOpen(false)
      loadRiders()
      loadStats()
    } catch (error: any) {
      logger.error('Error updating referrer:', error)
      setSnackbar({ open: true, message: error?.message || 'Failed to update referrer', severity: 'error' })
    } finally {
      setUpdatingReferrer(false)
    }
  }, [selectedRiderForReferrerUpdate, referrerEmailInput, loadRiders, loadStats])

  const handleCloseUpdateReferrerDialog = useCallback(() => {
    setUpdateReferrerDialogOpen(false)
    setSelectedRiderForReferrerUpdate(null)
    setReferrerEmailInput('')
    setUpdatingReferrer(false)
  }, [])

  // Memoized utility functions
  const getStatusColor = useCallback((status: string, statuses: typeof REGISTRATION_STATUSES) => {
    const statusConfig = statuses.find(s => s.value === status)
    return statusConfig?.color || 'default'
  }, [])

  const getStatusLabel = useCallback((status: string, statuses: typeof REGISTRATION_STATUSES) => {
    const statusConfig = statuses.find(s => s.value === status)
    return statusConfig?.label || status
  }, [])

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount)
  }, [])

  const maskPhoneNumber = useCallback((phone: string) => {
    // Mask first 6 digits: 9876543210 -> ******3210
    if (!phone || phone.length < 6) return phone
    return '******' + phone.slice(6)
  }, [])

  const getVehicleAssignmentStatus = useCallback((rider: Rider | any) => {
    if (rider.assignedVehicleId || rider.assignedVehicle) {
      return { label: 'Assigned', color: 'success' as const }
    }

    if (rider.vehicleReturnedDate || rider.assignedVehicle?.returnedDate) {
      return { label: 'Returned', color: 'warning' as const }
    }

    return { label: 'Not Assigned', color: 'default' as const }
  }, [])

  const riderStatCards = [
    {
      label: 'Total Riders',
      value: stats.totalRiders,
      helper: 'Registered rider base',
      icon: <PersonIcon />,
      main: '#2563eb',
      soft: '#dbeafe',
    },
    {
      label: 'Active Riders',
      value: stats.activeRiders,
      helper: 'Ready for operations',
      icon: <CheckCircleIcon />,
      main: '#059669',
      soft: '#d1fae5',
    },
    {
      label: 'Pending KYC',
      value: stats.pendingKYC,
      helper: 'Needs verification',
      icon: <AssignmentIcon />,
      main: '#d97706',
      soft: '#fef3c7',
    },
    {
      label: 'Own Vehicle',
      value: stats.ownVehicle,
      helper: 'IC / personal fleet',
      icon: <DirectionsBike />,
      main: '#0d9488',
      soft: '#ccfbf1',
    },
    {
      label: 'Vehicle Assigned',
      value: stats.vehicleAssignedRiders,
      helper: 'Riders with assigned vehicles',
      icon: <DirectionsBike />,
      main: '#7c3aed',
      soft: '#ede9fe',
    },
  ]

  return (
    <Box sx={{ minHeight: '95vh', p: { xs: 0.5, sm: 1.5, md: 3 }, pb: { xs: 3, md: 6 }, overflowX: 'hidden' }}>
      <Box
        sx={{
          mb: 2,
          p: { xs: 1.25, md: 1.75 },
          borderRadius: 2,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.98) 0%, rgba(13, 148, 136, 0.96) 52%, rgba(5, 150, 105, 0.94) 100%)',
          color: '#fff',
          boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)',
            backgroundSize: '44px 44px',
            opacity: 0.24,
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'flex-start' }}
          spacing={{ xs: 1.5, md: 2.5 }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<PersonIcon />}
                label="Rider Service"
                size="small"
                sx={{
                  color: '#ecfdf5',
                  backgroundColor: 'rgba(15, 23, 42, 0.22)',
                  border: '1px solid rgba(167, 243, 208, 0.35)',
                  fontWeight: 800,
                  '& .MuiChip-icon': { color: '#bbf7d0' },
                }}
              />
              <Chip
                label={`${totalCount.toLocaleString()} listed`}
                size="small"
                sx={{
                  color: '#e0f2fe',
                  backgroundColor: 'rgba(255,255,255,0.14)',
                  border: '1px solid rgba(186, 230, 253, 0.35)',
                  fontWeight: 800,
                }}
              />
            </Stack>
            <Typography variant="h4" component="h1" sx={{ mt: 1.5, fontWeight: 900, lineHeight: 1.08, fontSize: { xs: '1.55rem', md: '2.125rem' } }}>
              Rider Management
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,0.82)', maxWidth: 720, fontWeight: 500 }}>
              A focused operating view for onboarding, KYC, vehicle preference, exports, and rider records.
            </Typography>
          </Box>

          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            justifyContent={{ xs: 'stretch', sm: 'flex-start', lg: 'flex-end' }}
            sx={{
              '& .MuiButton-root': {
                flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' },
                minWidth: { xs: 0, sm: 64 },
                px: { xs: 1, md: 2 },
              },
            }}
          >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadRiders()
              loadStats()
            }}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.38)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
            }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<AssignmentIcon />}
            onClick={() => navigate('/vehicles/deployment-tickets')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.38)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
            }}
          >
            Deployment Tickets
          </Button>
          <Button
            variant="outlined"
            startIcon={<DirectionsBike />}
            onClick={() => navigate('/vehicles/return-tickets')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.38)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
            }}
          >
            Return Tickets
          </Button>
          <Button
            variant="outlined"
            startIcon={<BusinessIcon />}
            onClick={() => navigate('/city-heads')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.38)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
            }}
          >
            City Heads
          </Button>
          <Button
            variant="outlined"
            startIcon={<PersonIcon />}
            onClick={() => navigate('/field-recruiters')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.38)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
            }}
          >
            Field Recruiters
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => navigate('/riders/bulk-import')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.38)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
            }}
          >
            Import
          </Button>
          <Tooltip
            title={
              canExportRiders
                ? 'Export riders'
                : 'Only Super Admin, IT_Admin, and MIS_Management can export riders'
            }
          >
            <span>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportRiders}
                disabled={!canExportRiders}
                sx={{
                  color: '#fff',
                  borderColor: 'rgba(255,255,255,0.38)',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(255,255,255,0.16)' },
                  '&.Mui-disabled': {
                    color: 'rgba(255,255,255,0.38)',
                    borderColor: 'rgba(255,255,255,0.18)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                  },
                }}
              >
                Export
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              color: '#0f172a',
              backgroundColor: '#fff',
              fontWeight: 800,
              boxShadow: '0 12px 30px rgba(15, 23, 42, 0.22)',
              '&:hover': { backgroundColor: '#f8fafc' },
            }}
          >
            Add Rider
          </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={{ xs: 1, md: 1.5 }} sx={{ mb: 2 }}>
        {riderStatCards.map((item) => (
          <Grid item xs={6} sm={6} md={2.4} key={item.label}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderTop: `3px solid ${item.main}`,
                  pointerEvents: 'none',
                },
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  right: -36,
                  top: -38,
                  width: 118,
                  height: 118,
                  borderRadius: '50%',
                  background: item.soft,
                  opacity: 0.74,
                },
                '&:hover': {
                  transform: 'translateY(-3px)',
                  borderColor: item.main,
                  boxShadow: '0 18px 42px rgba(15, 23, 42, 0.12)',
                },
              }}
            >
              <CardContent sx={{ p: { xs: 1.4, md: 2.5 }, position: 'relative', zIndex: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 }}>
                      {item.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={900} sx={{ mt: 0.75, color: item.main, lineHeight: 1, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                      {item.value.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {item.helper}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: { xs: 28, md: 32 },
                      height: { xs: 28, md: 32 },
                      borderRadius: 2,
                      background: item.soft,
                      color: item.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 auto',
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>


      {/* Filters */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Rider Directory
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Search and filter rider records without leaving the service home page.
              </Typography>
            </Box>
          </Stack>
          <Grid container spacing={1} alignItems="center" wrap="wrap">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    applySearch();
                  }
                }}
                placeholder="Name, Phone, Rider ID, Client ID..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={() => {
                          applySearch();
                        }}
                      >
                        Search
                      </Button>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>KYC Status</InputLabel>
                <Select
                  size="small"
                  value={kycStatusFilter}
                  label="KYC Status"
                  onChange={(e) => {
                    setKycStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {KYC_STATUSES.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Rider Status</InputLabel>
                <Select
                  size="small"
                  value={activeStatusFilter}
                  label="Rider Status"
                  onChange={(e) => {
                    setActiveStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Autocomplete
                size="small"
                multiple
                options={cities}
                getOptionLabel={(option) => option.name}
                value={cities.filter((city) => cityFilter.includes(city.name))}
                onChange={(_, newValue) => {
                  setCityFilter(newValue.map((city) => city.name));
                  setPage(0);
                }}
                loading={loadingCities}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="City"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  size="small"
                  value={vehicleTypeFilter}
                  label="Vehicle Type"
                  onChange={(e) => {
                    setVehicleTypeFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="EV_VEHICLE">EV Vehicle</MenuItem>
                  <MenuItem value="OWN_VEHICLE">Own Vehicle</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8} md={3}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  fullWidth
                  size="small"
                  label="From"
                  type="date"
                  value={dateFromFilter}
                  onChange={(e) => {
                    setDateFromFilter(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="To"
                  type="date"
                  value={dateToFilter}
                  onChange={(e) => {
                    setDateToFilter(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                size="small"
                options={referredByOptions}
                getOptionLabel={(option) => option.name || option.phone || option.publicRiderId || option.id || 'Unknown'}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={referredByOptions.find(r => r.id === referredByFilter) || null}
                onChange={(_, newValue) => {
                  setReferredByFilter(newValue ? newValue.id : '');
                  setPage(0);
                }}
                loading={loadingReferredBy}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Referred By"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingReferredBy ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                sx={{ height: '100%' }}
                onClick={() => {
                  window.sessionStorage.removeItem(RIDER_MANAGEMENT_STATE_KEY)
                  setSearchInput('')
                  setSearchTerm('')
                  setKycStatusFilter('')
                  setActiveStatusFilter('')
                  setCityFilter([])
                  setVehicleTypeFilter('')
                  setReferredByFilter('')
                  setDateFromFilter('')
                  setDateToFilter('')
                  setPage(0)
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Active Filters Indicator */}
      {filtersApplied && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
              <Button
              color="inherit"
              size="small"
              onClick={() => {
                window.sessionStorage.removeItem(RIDER_MANAGEMENT_STATE_KEY);
                setSearchInput('');
                setSearchTerm('');
                setKycStatusFilter('');
                setActiveStatusFilter('');
                setCityFilter([]);
                setVehicleTypeFilter('');
                setReferredByFilter('');
                setDateFromFilter('');
                setDateToFilter('');
                setPage(0);
              }}
            >
              Clear All
            </Button>
          }
        >
          Filters are currently active. Results are filtered based on your criteria.
        </Alert>
      )}

      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        {loading ? (
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 3, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Card>
        ) : riders.length === 0 ? (
          <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No riders found</Typography>
          </Card>
        ) : (
          <Stack spacing={1.25}>
            {riders.map((rider) => (
              <Card
                key={rider.id}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" spacing={1.25} alignItems="flex-start">
                    <Avatar
                      src={rider.selfie || undefined}
                      onClick={() => handleViewRider(rider.id)}
                      sx={{
                        bgcolor: 'primary.main',
                        border: `2px solid ${rider.isActive ? '#4caf50' : '#f44336'}`,
                        width: 44,
                        height: 44,
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      {!rider.selfie && (rider.name ? rider.name.charAt(0).toUpperCase() : rider.phone.charAt(0))}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={800} noWrap onClick={() => handleViewRider(rider.id)} sx={{ cursor: 'pointer' }}>
                            {rider.name || 'No Name'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {maskPhoneNumber(rider.phone)}
                          </Typography>
                        </Box>
                        <Chip
                          label={getStatusLabel(rider.kycStatus, KYC_STATUSES)}
                          color={getStatusColor(rider.kycStatus, KYC_STATUSES) as any}
                          size="small"
                          sx={{ fontWeight: 700, maxWidth: 112 }}
                        />
                      </Stack>

                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        {[
                          ['City', rider.city || '-'],
                          ['Client', rider.clientName || '-'],
                          ['Vehicle', rider.needsEvRental === true ? 'EV Vehicle' : rider.needsEvRental === false ? 'Own Vehicle' : 'Unknown'],
                          ['Vehicle Status', getVehicleAssignmentStatus(rider).label],
                          ['Date', rider.createdAt ? new Date(rider.createdAt).toLocaleDateString('en-IN', { month: 'short', day: '2-digit' }) : '-'],
                        ].map(([label, value]) => (
                          <Grid item xs={6} key={label}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 700 }}>
                              {label}
                            </Typography>
                            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                              {value}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>

                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ mt: 1 }}>
                        <IconButton size="small" onClick={() => handleViewRider(rider.id)} aria-label="View rider">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleOpenDialog(rider)} aria-label="Edit rider">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Tooltip title={canEditReferral(user) ? 'Update Referrer' : 'Only Supply/Admin can edit referredById'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleUpdateReferrer(rider.id)}
                              aria-label="Update referrer"
                              disabled={!canEditReferral(user)}
                            >
                              <AssignmentIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {rider.registrationStatus === 'pending' && (
                          <>
                            <IconButton size="small" onClick={() => handleApproveRider(rider.id)} color="success" aria-label="Approve rider">
                              <CheckIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleRejectRider(rider.id)} color="error" aria-label="Reject rider">
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
        {totalCount > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{
              mt: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': {
                px: 1,
                flexWrap: 'wrap',
                justifyContent: 'center',
              },
            }}
          />
        )}
      </Box>

      {/* Riders Table */}
      <Card
        elevation={0}
        sx={{
          display: { xs: 'none', md: 'block' },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 16px 38px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'rgba(37, 99, 235, 0.06)' }}>
                <TableRow>
                  <TableCell>Rider</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>KYC Status</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Client Name</TableCell>
                  {/* <TableCell>Work Type</TableCell> */}
                  {/* <TableCell>Vehicle Type</TableCell> */}
                  {/* <TableCell>Vehicle</TableCell> */}
                  <TableCell>Vehicle Type</TableCell>
                  <TableCell>Vehicle Status</TableCell>
                  <TableCell>Referred By</TableCell>
                  <TableCell>Date</TableCell>
                  {/* <TableCell>Telecaller</TableCell> */}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : riders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ textAlign: 'center', py: 4 }}>
                      No riders found
                    </TableCell>
                  </TableRow>
                ) : (
                  riders.map((rider) => {
                    const vehicleAssignmentStatus = getVehicleAssignmentStatus(rider)

                    return (
                    <TableRow key={rider.id} sx={{ '& > td': { py: 1 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            onClick={() => handleViewRider(rider.id)}
                            sx={{
                              cursor: 'pointer',
                              position: 'relative',
                              '&:hover .avatar-overlay': {
                                opacity: 1,
                              }
                            }}
                          >
                            <Avatar
                              src={rider.selfie || undefined}
                              sx={{
                                bgcolor: 'primary.main',
                                border: `2px solid ${rider.isActive ? '#4caf50' : '#f44336'}`,
                                width: 48,
                                height: 48,
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                }
                              }}
                            >
                              {!rider.selfie && (rider.name ? rider.name.charAt(0).toUpperCase() : rider.phone.charAt(0))}
                            </Avatar>
                            {/* Status Indicator Badge */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: 0,
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: rider.isActive ? '#4caf50' : '#f44336',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              }}
                            />
                            {/* Hover Overlay */}
                            <Box
                              className="avatar-overlay"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                borderRadius: '50%',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                              }}
                            >
                              <ViewIcon sx={{ color: 'white', fontSize: 20 }} />
                            </Box>
                          </Box>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              onClick={() => handleViewRider(rider.id)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  color: 'primary.main',
                                  textDecoration: 'underline',
                                }
                              }}
                            >
                              {rider.name || 'No Name'}
                            </Typography>
                            <Chip
                              label={rider.publicRiderId || rider.id}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{
                                mt: 0.5,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: '20px'
                              }}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {maskPhoneNumber(rider.phone)}
                          </Typography>
                          {rider.phoneVerified && (
                            <Chip label="Verified" size="small" color="success" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(rider.kycStatus, KYC_STATUSES)}
                          color={getStatusColor(rider.kycStatus, KYC_STATUSES) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {rider.city || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {rider.clientName ? (
                          <Chip
                            label={rider.clientName}
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 700 }}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      {/* <TableCell>
                        {rider.workType ? (
                          <Chip
                            label={rider.workType === 'FULL_TIME' ? 'Full-Time' : 'Part-Time'}
                            color={rider.workType === 'FULL_TIME' ? 'primary' : 'info'}
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </TableCell> */}
                      {/* <TableCell>
                        {rider.needsEvRental === true ? (
                          <Chip label="Rented" color="info" size="small" />
                        ) : rider.needsEvRental === false && rider.vehiclePreference ? (
                          rider.vehiclePreference === 'OWN_VEHICLE' ? (
                            <Chip label="Own Vehicle" color="success" size="small" />
                          ) : rider.vehiclePreference === 'CYCLE' ? (
                            <Chip label="Cycle" color="success" size="small" />
                          ) : rider.vehiclePreference === 'WALK' ? (
                            <Chip label="Walk" color="success" size="small" />
                          ) : rider.vehiclePreference === 'RENTED_VEHICLE' ? (
                            <Chip label="Other Rental" color="info" size="small" />
                          ) : (
                            <Chip label={rider.vehiclePreference} color="default" size="small" />
                          )
                        ) : (
                          <Chip label="Unknown" color="default" size="small" />
                        )}
                      </TableCell> */}
                      {/* <TableCell>
                        {rider.assignedVehicle?.registrationNumber ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <Typography variant="body2" fontWeight={500}>
                              {rider.assignedVehicle.registrationNumber}
                            </Typography>
                          </Box>
                        ) : rider.assignedVehicleId ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <Chip
                              label="Assigned"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No Vehicle
                          </Typography>
                        )}
                      </TableCell> */}
                      <TableCell>
                        {rider.needsEvRental === true ? (
                          <Chip label="EV Vehicle" color="info" size="small" />
                        ) : rider.needsEvRental === false ? (
                          <Chip label="Own Vehicle" color="success" size="small" />
                        ) : (
                          <Chip label="Unknown" color="default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vehicleAssignmentStatus.label}
                          color={vehicleAssignmentStatus.color}
                          size="small"
                          variant={vehicleAssignmentStatus.label === 'Not Assigned' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(rider as any).referredById || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {rider.createdAt ? new Date(rider.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: '2-digit' }) : '-'}
                        </Typography>
                      </TableCell>
                      {/* <TableCell>
                        {(rider as any).assignedTelecaller ? (
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {(rider as any).assignedTelecaller.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {(rider as any).assignedTelecaller.telecallerCode}
                            </Typography>
                          </Box>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedRiderForTelecaller(rider);
                              setTelecallerDialogOpen(true);
                            }}
                            sx={{ fontSize: '0.7rem', py: 0.5 }}
                          >
                            Assign
                          </Button>
                        )}
                      </TableCell> */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewRider(rider.id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Rider">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(rider)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={canEditReferral(user) ? 'Update Referrer' : 'Only Supply/Admin can edit referredById'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleUpdateReferrer(rider.id)}
                                disabled={!canEditReferral(user)}
                              >
                                <AssignmentIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {rider.registrationStatus === 'pending' && (
                            <>
                              <Tooltip title="Approve Rider">
                                <IconButton
                                  size="small"
                                  onClick={() => handleApproveRider(rider.id)}
                                  color="success"
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject Rider">
                                <IconButton
                                  size="small"
                                  onClick={() => handleRejectRider(rider.id)}
                                  color="error"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* MUI TablePagination Component - Same as VehicleInventory */}
          {totalCount > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => {
                logger.info('📄 Changing to page:', newPage);
                setPage(newPage);
              }}
              onRowsPerPageChange={(event) => {
                const newRowsPerPage = parseInt(event.target.value, 10);
                logger.info('📄 Rows per page changing:', {
                  oldValue: rowsPerPage,
                  newValue: newRowsPerPage,
                  resetPage: true
                });
                setRowsPerPage(newRowsPerPage);
                setPage(0);
              }}
              showFirstButton
              showLastButton
            />
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Rider Dialog */}
      <EnhancedRiderForm
        open={openDialog}
        rider={editingRider}
        onClose={handleCloseDialog}
        onSave={handleSaveRider}
      />

      {/* Telecaller Assignment Dialog */}
      <TelecallerAssignmentDialog
        open={telecallerDialogOpen}
        onClose={() => {
          setTelecallerDialogOpen(false);
          setSelectedRiderForTelecaller(null);
        }}
        rider={selectedRiderForTelecaller as any}
        onAssignmentSuccess={(assignedTelecaller?: any, riderId?: string) => {
          // Debug: log the assigned telecaller and rider id for troubleshooting
          logger.debug('Telecaller assignment callback:', { assignedTelecaller, riderId });

          // Optimistically update the local row so the telecaller name appears immediately
          if (assignedTelecaller && riderId) {
            setRiders((prev) => prev.map((r) => (r.id === riderId ? { ...r, assignedTelecaller } : r)));
          }

          // Refresh the list to ensure data is fully in sync with the backend
          loadRiders(false);

          setSnackbar({
            open: true,
            message: 'Telecaller assignment updated successfully',
            severity: 'success',
          });
        }}
      />

      {/* Update Referrer Dialog */}
      <Dialog
        open={updateReferrerDialogOpen}
        onClose={handleCloseUpdateReferrerDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.25rem',
            fontWeight: 700,
            pb: 1,
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.96) 0%, rgba(13, 148, 136, 0.94) 100%)',
            color: '#fff',
          }}
        >
          Update FR/Source
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                Rider Information
              </Typography>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  backgroundColor: 'rgba(37, 99, 235, 0.04)',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Rider Name
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedRiderForReferrerUpdate?.name || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Rider ID
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                      {selectedRiderForReferrerUpdate?.publicRiderId || selectedRiderForReferrerUpdate?.id || '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Current FR/Source
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {selectedRiderForReferrerUpdate?.referredById || 'Not assigned'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                Update FR/Source Mail ID
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter FR/Source Email ID (e.g., john.doe@company.com)"
                value={referrerEmailInput}
                onChange={(e) => setReferrerEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && referrerEmailInput.trim() && !updatingReferrer) {
                    handleConfirmUpdateReferrer()
                  }
                }}
                type="email"
                disabled={updatingReferrer}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ color: 'primary.main', display: 'flex' }}>
                        📧
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Please enter a valid email address for the FR/Source
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCloseUpdateReferrerDialog}
            variant="outlined"
            disabled={updatingReferrer}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpdateReferrer}
            variant="contained"
            disabled={!referrerEmailInput.trim() || updatingReferrer}
            sx={{
              background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.96) 0%, rgba(13, 148, 136, 0.94) 100%)',
            }}
          >
            {updatingReferrer ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              'Update Referrer'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RiderManagement
